import type { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { resolveLoadedDay } from "../components/AddNurse/addStaffUtils";
import { FEATURE_FLAGS } from "../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { getStaffingDate } from "../utils/dateUtils";
import { isNurseActiveInDepartment } from "../utils/formatters";
import type { AutoApproveRule, RequestOutreach, ShiftRequest } from "../utils/models";
import { DIFF_HOURS_WARNING } from "../utils/warningDialogs/utils";
import {
	adjustPlan,
	approve,
	callNow,
	type DemoState,
	decline,
	pause,
	readOutreach,
	resetPlan,
	resume,
	skip,
	textAnyway,
	textNow,
	undoSkip,
} from "./demoOutreach/demoOutreachPlan";
import { splitRoster } from "./demoOutreach/demoOutreachRoster";
import type { DemoContext } from "./demoOutreach/demoOutreachSeed";
import { initialStateOf } from "./demoOutreach/demoOutreachState";
import { useNowEveryMinute } from "./useNowEveryMinute";
import { useFeatureFlag } from "./useOpenFeatureFlag";
import type { RequestOutreachActions } from "./useRequestOutreach";

/**
 * DEMO ONLY (APP-933). A request's outreach built from the real request and roster, seeded from the request id, with
 * every action on local state: nothing here makes a network call. The real-data work (APP-918) deletes this file and
 * `demoOutreach/`, and points `useRequestOutreach` at the backend.
 */

/**
 * Session-wide, so closing and reopening the popup keeps what the user did; a reload starts over from the seed.
 */
const STORE = new Map<string, DemoState>();

type StoreEntry = { key: string; state: DemoState };

type SeedInputs = Pick<
	ReturnType<typeof useHospitalData>,
	"hospitalData" | "chosenDepartment" | "timezone" | "getNurseFormattedName" | "getWeekIndexByDate" | "getDayIndexByDate"
> & { jobManagementEnabled: boolean; autoApprove: boolean; autoApproveRules: readonly AutoApproveRule[]; now: DateTime };

// Active includes On Leave and Suspended: whether they can work this shift is the roster split's call.
const rosterOf = ({ hospitalData, chosenDepartment }: SeedInputs) =>
	hospitalData.nurses.filter((nurse) => isNurseActiveInDepartment(nurse, chosenDepartment.department_id)).sort((a, b) => a.id - b.id);

/**
 * Keyed on what the seed reads, so a new response, other times, roles or headcount, a roster or department setting
 * change seeds afresh rather than show a stale copy (the user's local actions on the old copy go with it). The loaded
 * weeks are left out on purpose: they refetch with every schedule change, which would wipe the demo mid-click.
 */
const storeKeyOf = (request: ShiftRequest, inputs: SeedInputs): string => {
	const { message } = request;
	const { chosenDepartment: department } = inputs;
	const responses = message.request_data.map(({ nurse_id: nurseId, status }) => `${nurseId}:${status}`).sort();
	return JSON.stringify([
		request.id,
		message.shift_start.toMillis(),
		message.shift_end.toMillis(),
		[...message.roles].sort(),
		request.job_id,
		request.charge_only,
		// A replace request has no headcount of its own (it replaces one person), so it keys on none.
		"needed_nurses" in request ? request.needed_nurses : null,
		responses,
		rosterOf(inputs).map((nurse) => nurse.id),
		inputs.timezone,
		department.dayStart,
		department.scheduled_shift_delta_minutes,
		department.longShiftDuration,
		inputs.jobManagementEnabled,
		inputs.autoApprove,
		inputs.autoApproveRules,
	]);
};

const seedStateOf = (request: ShiftRequest, inputs: SeedInputs): DemoState => {
	const { hospitalData, chosenDepartment, timezone, getNurseFormattedName } = inputs;
	const departmentId = chosenDepartment.department_id;
	const roster = rosterOf(inputs);
	const graceMinutes = chosenDepartment.scheduled_shift_delta_minutes;
	const staffingDate = getStaffingDate(request.message.shift_start.setZone(timezone), chosenDepartment.dayStart, graceMinutes);
	const split = splitRoster({
		request,
		roster,
		departmentId,
		dayStart: chosenDepartment.dayStart,
		graceMinutes,
		jobManagementEnabled: inputs.jobManagementEnabled,
		// DEFAULT: the same long-day limit the daily-hours warning dialog falls back to (useNurseWarnings).
		longShiftHours: chosenDepartment.longShiftDuration ?? DIFF_HOURS_WARNING,
		weeks: hospitalData.weeks,
		loadedDay: resolveLoadedDay(staffingDate, inputs),
		nameOf: getNurseFormattedName,
	});
	const ctx: DemoContext = {
		nurses: hospitalData.nurses,
		nameOf: getNurseFormattedName,
		timezone,
		now: inputs.now,
		autoApprove: inputs.autoApprove,
		autoApproveRules: inputs.autoApproveRules,
	};
	return initialStateOf(request, ctx, split);
};

export type DemoRequestOutreach = { outreach: RequestOutreach; actions: RequestOutreachActions };

/** The demo outreach for `request`, or `null` when `enabled` is false (so a real department never builds the roster). */
export const useDemoRequestOutreach = (request: ShiftRequest, enabled: boolean): DemoRequestOutreach | null => {
	const hospital = useHospitalData();
	const { chosenDepartment, timezone } = hospital;
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	// DEFAULT: `autoApproveRules` is optional on the department; absent means none are configured.
	const autoApproveRules = chosenDepartment.autoApproveRules ?? [];
	const autoApprove = useFeatureFlag(FEATURE_FLAGS.AutoApproveShifts) && autoApproveRules.length > 0;
	// One clock for what's derived and what's done: the view and every action read the same minute.
	const minute = useNowEveryMinute();
	const now = useMemo(() => minute.setZone(timezone), [minute, timezone]);
	const inputs: SeedInputs = { ...hospital, jobManagementEnabled, autoApprove, autoApproveRules, now };
	const key = storeKeyOf(request, inputs);

	const loadOrSeed = (): DemoState => {
		const stored = STORE.get(key);
		if (stored !== undefined) return stored;
		const created = seedStateOf(request, inputs);
		STORE.set(key, created);
		return created;
	};

	const [entry, setEntry] = useState<StoreEntry | null>(() => (enabled ? { key, state: loadOrSeed() } : null));
	// The real inputs changed under the open popup: switch to their own entry, seeding it if it's new.
	if (enabled && entry?.key !== key) setEntry({ key, state: loadOrSeed() });

	const update = useCallback(
		(change: (current: DemoState, at: DateTime) => DemoState) =>
			setEntry((current) => {
				if (current === null) throw new Error("Demo outreach: an action ran on a department without demo outreach");
				const next = change(current.state, now);
				STORE.set(current.key, next);
				return { key: current.key, state: next };
			}),
		[now],
	);

	const actions = useMemo<RequestOutreachActions>(
		() => ({
			pause: () => update((current) => pause(current)),
			resume: () => update((current, at) => resume(current, at)),
			textNow: (nurseId) => update((current, at) => textNow(current, nurseId, at)),
			callNow: (nurseId) => update((current, at) => callNow(current, nurseId, at)),
			skip: (nurseId) => update((current, at) => skip(current, nurseId, at)),
			undoSkip: (nurseId) => update((current, at) => undoSkip(current, nurseId, at)),
			textAnyway: (nurseId) => update((current, at) => textAnyway(current, nurseId, at)),
			adjustPlan: (choice) => update((current, at) => adjustPlan(current, choice, at)),
			resetPlan: () => update((current, at) => resetPlan(current, at)),
			approve: (nurseId) => update((current, at) => approve(current, nurseId, at)),
			decline: (nurseId) => update((current, at) => decline(current, nurseId, at)),
		}),
		[update],
	);

	const state = entry !== null && entry.key === key ? entry.state : null;
	const outreach = useMemo(() => (state === null ? null : readOutreach(state, now)), [state, now]);
	return outreach === null ? null : { outreach, actions };
};
